/**
 * Masonry Gallery Component with Isotope.js
 * Handles the initialization and management of masonry gallery layouts with perfect bottom alignment
 */

class MasonryGallery {
  constructor(container) {
    this.container = container;
    this.isotope = null;
    this.imagesLoaded = false;
    this.isInitialized = false;
    
    // Configuration from data attributes
    this.config = {
      baseGutter: parseInt(container.dataset.masonryGutter) || 16,
      columnsMobile: parseInt(container.dataset.masonryColumnsMobile) || 1,
      columnsTablet: parseInt(container.dataset.masonryColumnsTablet) || 2,
      columnsDesktop: parseInt(container.dataset.masonryColumnsDesktop) || 3,
      enableAnimation: container.dataset.masonryAnimation === 'true',
      enableBottomAlign: container.dataset.masonryBottomAlign === 'true',
      heightTolerance: parseInt(container.dataset.masonryHeightTolerance) || 50,
      layoutMode: container.dataset.masonryLayoutMode || 'masonry'
    };

    this.init();
  }

  /**
   * Initialize the masonry gallery
   */
  async init() {
    try {
      // Check layout mode
      if (this.config.layoutMode === 'equal-height') {
        // Use CSS Grid equal height mode
        this.initializeEqualHeightMode();
      } else if (this.config.layoutMode === 'grid-masonry') {
        // Use CSS Grid masonry mode with object-fit cover
        this.initializeGridMasonryMode();
      } else {
        // Wait for Isotope library to load
        await this.waitForIsotope();
        
        // Wait for images to load
        await this.waitForImages();
        
        // Initialize Isotope
        this.initializeIsotope();
        
        // Setup event listeners
        this.setupEventListeners();
        
        // Trigger animations if enabled
        if (this.config.enableAnimation) {
          this.animateItems();
        }
      }
      
      this.isInitialized = true;
      this.hideLoadingSpinner();
      
    } catch (error) {
      console.error('Failed to initialize masonry gallery:', error);
      this.fallbackToCSS();
    }
  }

  /**
   * Wait for Isotope library to be available
   */
  waitForIsotope() {
    return new Promise((resolve, reject) => {
      let attempts = 0;
      const maxAttempts = 100; // 10 seconds max wait
      
      const checkIsotope = () => {
        console.log(`Attempt ${attempts}: Isotope=${typeof window.Isotope}, imagesLoaded=${typeof window.imagesLoaded}`);
        
        if (window.Isotope && window.imagesLoaded) {
          console.log('Isotope and imagesLoaded libraries found!');
          resolve();
        } else if (attempts < maxAttempts) {
          attempts++;
          setTimeout(checkIsotope, 100);
        } else {
          console.error('Isotope library not found after maximum attempts');
          reject(new Error('Isotope library not found'));
        }
      };
      
      checkIsotope();
    });
  }

  /**
   * Wait for all images to load using imagesLoaded plugin
   */
  waitForImages() {
    return new Promise((resolve) => {
      if (window.imagesLoaded) {
        window.imagesLoaded(this.container, () => {
          this.imagesLoaded = true;
          console.log('All images loaded successfully');
          resolve();
        });
      } else {
        // Fallback if imagesLoaded is not available
        const images = this.container.querySelectorAll('img');
        let loadedImages = 0;
        
        if (images.length === 0) {
          resolve();
          return;
        }
        
        images.forEach(img => {
          if (img.complete) {
            loadedImages++;
            if (loadedImages === images.length) {
              resolve();
            }
          } else {
            img.addEventListener('load', () => {
              loadedImages++;
              if (loadedImages === images.length) {
                resolve();
              }
            });
            img.addEventListener('error', () => {
              loadedImages++;
              if (loadedImages === images.length) {
                resolve();
              }
            });
          }
        });
      }
    });
  }

  /**
   * Get current gutter size based on screen size (matches CSS logic)
   */
  getCurrentGutter() {
    const width = window.innerWidth;
    
    // Match the mobile media query from CSS
    if (width <= 749) {
      return Math.max(this.config.baseGutter * 0.5, 1); // Half gutter on mobile, minimum 1px
    } else {
      return Math.max(this.config.baseGutter, 1); // Full gutter on tablet/desktop, minimum 1px
    }
  }

  /**
   * Initialize Isotope layout
   */
  initializeIsotope() {
    // Clear any existing CSS Grid properties
    this.container.style.display = '';
    this.container.style.gridTemplateColumns = '';
    this.container.style.gap = '';
    
    const currentColumns = this.getCurrentColumns();
    const currentGutter = this.getCurrentGutter();
    const columnWidth = this.calculateColumnWidth(currentColumns, currentGutter);
    
    // Determine the layout mode to use
    let layoutMode = this.config.layoutMode;
    
    // If bottom alignment is enabled and we're using masonry, switch to fitRows
    if (this.config.enableBottomAlign && layoutMode === 'masonry') {
      layoutMode = 'fitRows';
      console.log('Bottom alignment enabled - switching to fitRows layout mode');
    }
    
    // Special handling for fitRows mode (either directly selected or via bottom alignment)
    if (layoutMode === 'fitRows') {
      // Use fitRows layout options
      this.isotope = new window.Isotope(this.container, {
        itemSelector: '.masonry-gallery__item',
        layoutMode: 'fitRows',
        fitRows: {
          gutter: currentGutter
        },
        transitionDuration: '0.3s',
        resize: true,
        initLayout: false // Don't layout immediately, we'll do it manually
      });
    } else {
      // Default masonry layout options
      this.isotope = new window.Isotope(this.container, {
        itemSelector: '.masonry-gallery__item',
        layoutMode: 'masonry',
        masonry: {
          gutter: currentGutter,
          columnWidth: columnWidth,
          horizontalOrder: true,
          fitWidth: true
        },
        percentPosition: false,
        transitionDuration: '0.3s',
        resize: true,
        initLayout: false // Don't layout immediately, we'll do it manually
      });
    }

    // Add initialized class for styling
    this.container.classList.add('masonry-initialized');
    
    // Now trigger the initial layout
    this.isotope.layout();
  }

  /**
   * Get current number of columns based on screen size
   */
  getCurrentColumns() {
    const width = window.innerWidth;
    
    if (width >= 990) {
      return this.config.columnsDesktop;
    } else if (width >= 750) {
      return this.config.columnsTablet;
    } else {
      return this.config.columnsMobile;
    }
  }

  /**
   * Calculate column width based on container width and number of columns
   */
  calculateColumnWidth(columns, gutter = null) {
    const currentGutter = gutter || this.getCurrentGutter();
    const containerWidth = this.container.offsetWidth;
    const gutterSpace = (columns - 1) * currentGutter;
    const columnWidth = (containerWidth - gutterSpace) / columns;
    
    // Set fixed width on items for Isotope to work properly
    const items = this.container.querySelectorAll('.masonry-gallery__item');
    items.forEach(item => {
      item.style.width = `${columnWidth}px`;
    });
    
    return columnWidth;
  }

  /**
   * Setup event listeners for responsive behavior
   */
  setupEventListeners() {
    // Optimized debounce function
    const debounce = (func, wait) => {
      let timeout;
      return function executedFunction(...args) {
        const later = () => {
          clearTimeout(timeout);
          func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
      };
    };
    
    // Debounced resize handler
    const debouncedResize = debounce(() => {
      this.handleResize();
    }, 150);
    
    window.addEventListener('resize', debouncedResize, { passive: true });

    // Handle dynamic content changes (for Shopify theme editor)
    if (window.Shopify && window.Shopify.designMode) {
      document.addEventListener('shopify:section:load', (event) => {
        if (event.target.contains(this.container)) {
          this.refresh();
        }
      });

      document.addEventListener('shopify:block:select', (event) => {
        if (this.container.contains(event.target)) {
          this.refresh();
        }
      });
    }

    // Setup lazy loading for images
    this.setupLazyLoading();
  }

  /**
   * Handle window resize
   */
  handleResize() {
    if (this.config.layoutMode === 'equal-height') {
      this.updateEqualHeightLayout();
      return;
    }
    
    if (this.config.layoutMode === 'grid-masonry') {
      this.updateGridMasonryLayout();
      return;
    }

    if (!this.isotope || !this.isInitialized) return;

    const newColumns = this.getCurrentColumns();
    const newGutter = this.getCurrentGutter();
    const newColumnWidth = this.calculateColumnWidth(newColumns, newGutter);
    
    // Determine if we're using fitRows or masonry
    const isUsingFitRows = this.config.layoutMode === 'fitRows' || 
                          (this.config.enableBottomAlign && this.config.layoutMode === 'masonry');
    
    if (isUsingFitRows) {
      // Update fitRows options
      this.isotope.options.fitRows = {
        gutter: newGutter
      };
    } else {
      // Update masonry options with new responsive gutter values
      this.isotope.options.masonry = {
        ...this.isotope.options.masonry,
        columnWidth: newColumnWidth,
        gutter: newGutter
      };
    }
    
    // Reprocess all items and layout
    this.isotope.reloadItems();
    this.isotope.layout();
  }

  /**
   * Setup lazy loading for images
   */
  setupLazyLoading() {
    const images = this.container.querySelectorAll('img[data-src]');
    
    if ('IntersectionObserver' in window) {
      const imageObserver = new IntersectionObserver((entries, observer) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const img = entry.target;
            // Use requestAnimationFrame for smoother loading
            requestAnimationFrame(() => {
              this.loadImage(img);
              observer.unobserve(img);
            });
          }
        });
      }, {
        rootMargin: '50px 0px',
        threshold: 0.01
      });

      images.forEach(img => imageObserver.observe(img));
    } else {
      // Fallback for browsers without IntersectionObserver
      images.forEach(img => this.loadImage(img));
    }
  }

  /**
   * Load an individual image
   */
  loadImage(img) {
    if (img.dataset.src) {
      img.src = img.dataset.src;
      
      if (img.dataset.srcset) {
        img.srcset = img.dataset.srcset;
      }
      
      if (img.dataset.sizes) {
        img.sizes = img.dataset.sizes;
      }

      img.addEventListener('load', () => {
        img.classList.add('lazyloaded');
        if (this.isotope) {
          // Use imagesLoaded to ensure proper layout after image loads
          if (window.imagesLoaded) {
            window.imagesLoaded(img.parentElement, () => {
              this.isotope.layout();
            });
          } else {
            this.isotope.layout();
          }
        }
      });

      img.addEventListener('error', () => {
        img.classList.add('lazyerror');
        if (this.isotope) {
          this.isotope.layout();
        }
      });
      
      // Remove data attributes
      delete img.dataset.src;
      delete img.dataset.srcset;
      delete img.dataset.sizes;
    }
  }

  /**
   * Animate items on load
   */
  animateItems() {
    const items = this.container.querySelectorAll('.masonry-gallery__item');
    
    items.forEach((item, index) => {
      setTimeout(() => {
        item.classList.add('animate-in');
      }, index * 100);
    });
  }

  /**
   * Hide loading spinner
   */
  hideLoadingSpinner() {
    const loadingSpinner = this.container.parentNode.querySelector('.masonry-gallery__loading');
    if (loadingSpinner) {
      loadingSpinner.classList.add('hidden');
      setTimeout(() => {
        loadingSpinner.remove();
      }, 300);
    }
  }

  /**
   * Fallback to CSS grid layout if Isotope fails
   */
  fallbackToCSS() {
    console.warn('Falling back to CSS grid layout');
    this.container.classList.add('masonry-fallback');
    this.container.classList.remove('masonry-initialized');
    
    // Ensure grid layout is visible
    this.container.style.opacity = '1';
    
    this.hideLoadingSpinner();
    
    if (this.config.enableAnimation) {
      this.animateItems();
    }
  }

  /**
   * Refresh the masonry layout
   */
  refresh() {
    if (this.isotope) {
      this.isotope.reloadItems();
      this.isotope.layout();
    }
  }

  /**
   * Initialize grid masonry mode using CSS Grid with varied heights
   */
  initializeGridMasonryMode() {
    this.container.classList.add('grid-masonry-mode');
    this.container.classList.add('masonry-initialized');
    
    // Assign grid spans based on image aspect ratios
    this.assignGridSpans();
    
    // Set CSS custom properties
    this.updateGridMasonryLayout();
    
    // Setup lazy loading
    this.setupLazyLoading();
    
    // Setup event listeners for responsive behavior
    this.setupEventListeners();
    
    // Trigger animations if enabled
    if (this.config.enableAnimation) {
      this.animateItems();
    }
  }

  /**
   * Assign grid span classes based on image aspect ratios
   */
  assignGridSpans() {
    const items = this.container.querySelectorAll('.masonry-gallery__item');
    const columns = this.getCurrentColumns();
    
    // Create a pattern that ensures rows complete evenly
    const patterns = {
      2: [3, 4, 4, 3, 2, 4], // For 2 columns
      3: [3, 4, 2, 4, 3, 3, 2, 4, 3], // For 3 columns  
      4: [3, 2, 4, 3, 2, 3, 4, 2], // For 4 columns
      5: [2, 3, 2, 3, 4, 3, 2, 3, 2, 4], // For 5 columns
      6: [2, 3, 2, 3, 2, 3, 3, 2, 3, 2, 3, 2] // For 6 columns
    };
    
    const pattern = patterns[columns] || patterns[3];
    
    items.forEach((item, index) => {
      // Get aspect ratio from data attribute
      const aspectRatio = parseFloat(item.dataset.aspectRatio) || 1;
      
      // Use pattern with aspect ratio consideration
      let baseSpan = pattern[index % pattern.length];
      
      // Adjust based on aspect ratio but keep within bounds
      if (aspectRatio > 1.5 && baseSpan > 2) {
        baseSpan = Math.max(2, baseSpan - 1); // Landscape images shorter
      } else if (aspectRatio < 0.7 && baseSpan < 4) {
        baseSpan = Math.min(4, baseSpan + 1); // Portrait images taller
      }
      
      const spanClass = `grid-span-${baseSpan}`;
      
      // Remove any existing span classes
      item.classList.remove('grid-span-2', 'grid-span-3', 'grid-span-4', 'grid-span-5');
      // Add the calculated span class
      item.classList.add(spanClass);
    });
    
    console.log(`Grid masonry: Assigned spans for ${items.length} items in ${columns} columns`);
  }

  /**
   * Update grid masonry layout calculations
   */
  updateGridMasonryLayout() {
    const columns = this.getCurrentColumns();
    const gutter = this.getCurrentGutter();
    
    // Apply CSS custom properties for the grid layout
    this.container.style.setProperty('--masonry-columns-desktop', this.config.columnsDesktop);
    this.container.style.setProperty('--masonry-columns-tablet', this.config.columnsTablet);
    this.container.style.setProperty('--masonry-columns-mobile', this.config.columnsMobile);
    this.container.style.setProperty('--masonry-gap', `${gutter}px`);
    
    console.log(`Grid masonry mode: ${columns} columns, ${gutter}px gap`);
  }

  /**
   * Initialize equal height mode using CSS Grid
   */
  initializeEqualHeightMode() {
    this.container.classList.add('masonry-equal-height');
    this.container.classList.add('masonry-initialized');
    
    // Calculate and set target row height
    this.updateEqualHeightLayout();
    
    // Setup lazy loading
    this.setupLazyLoading();
    
    // Setup event listeners for responsive behavior
    this.setupEventListeners();
    
    // Trigger animations if enabled
    if (this.config.enableAnimation) {
      this.animateItems();
    }
  }

  /**
   * Update equal height layout calculations
   */
  updateEqualHeightLayout() {
    const columns = this.getCurrentColumns();
    const gutter = this.getCurrentGutter();
    const containerWidth = this.container.offsetWidth;
    const itemWidth = (containerWidth - (columns - 1) * gutter) / columns;
    
    // Calculate average aspect ratio from items if available
    const items = this.container.querySelectorAll('.masonry-gallery__item[data-aspect-ratio]');
    let avgAspectRatio = 1.2; // Default 5:6 aspect ratio
    
    if (items.length > 0) {
      const totalAspectRatio = Array.from(items).reduce((sum, item) => {
        return sum + parseFloat(item.dataset.aspectRatio || 1.2);
      }, 0);
      avgAspectRatio = totalAspectRatio / items.length;
    }
    
    // Set height based on average aspect ratio
    const targetHeight = itemWidth / avgAspectRatio;
    
    // Apply CSS custom properties for the grid layout
    this.container.style.setProperty('--masonry-item-height', `${targetHeight}px`);
    this.container.style.setProperty('--masonry-columns-desktop', this.config.columnsDesktop);
    this.container.style.setProperty('--masonry-columns-tablet', this.config.columnsTablet);
    this.container.style.setProperty('--masonry-columns-mobile', this.config.columnsMobile);
    this.container.style.setProperty('--masonry-gap', `${gutter}px`);
    
    console.log(`Equal height mode: ${columns} columns, ${targetHeight.toFixed(0)}px height, ${gutter}px gap`);
  }

  /**
   * Destroy the isotope instance
   */
  destroy() {
    if (this.isotope) {
      this.isotope.destroy();
      this.isotope = null;
    }
    this.isInitialized = false;
  }
}

/**
 * Auto-initialize masonry galleries on page load and in theme editor
 */
function initializeMasonryGalleries() {
  const galleries = document.querySelectorAll('[data-masonry-container]');
  
  galleries.forEach(gallery => {
    // Skip if already initialized
    if (gallery.masonryInstance) return;
    
    try {
      // Performance mark for initialization timing
      if (window.performance && window.performance.mark) {
        window.performance.mark('masonry-init-start');
      }
      
      gallery.masonryInstance = new MasonryGallery(gallery);
      
      // Log performance metrics
      if (window.performance && window.performance.mark) {
        window.performance.mark('masonry-init-end');
        window.performance.measure('masonry-init', 'masonry-init-start', 'masonry-init-end');
        const measure = window.performance.getEntriesByName('masonry-init')[0];
        console.log(`Masonry gallery initialized in ${measure.duration.toFixed(2)}ms`);
      }
    } catch (error) {
      console.error('Failed to initialize masonry gallery:', error);
      // Apply fallback CSS class for graceful degradation
      gallery.classList.add('masonry-fallback');
    }
  });
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeMasonryGalleries);
} else {
  initializeMasonryGalleries();
}

// Re-initialize for theme editor
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', () => {
    setTimeout(initializeMasonryGalleries, 100);
  });
}

// Export for manual initialization if needed
window.MasonryGallery = MasonryGallery;

// Debug helper: manually trigger layout refresh
window.debugMasonryLayout = function() {
  const galleries = document.querySelectorAll('[data-masonry-container]');
  galleries.forEach((gallery, index) => {
    if (gallery.masonryInstance) {
      console.log(`Refreshing gallery ${index}...`);
      gallery.masonryInstance.refresh();
    }
  });
};